import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  useGetAllInvoiceQuery,
  useCreateEmailInvoiceMutation,
  useGetWorkOrderQuery,
} from "../../../Api/attoDeskApi";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate } from "react-router";
import {
  ICompanyDetails,
  ICustomer,
  ICustomerProject,
  IInvoice,
} from "../../../Api/Interface/api.interface";
import dayjs, { Dayjs } from "dayjs";
import {
  ProductType,
  TransferType,
  WorkOrderStatus,
} from "../../../Core/Enum/enum";
import { useParams } from "react-router-dom";
import ReactToPrint from "react-to-print";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import EmailIcon from "@mui/icons-material/Email";
import { useNotifier } from "../../../Core/Notifier";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// ─── Helpers ────────────────────────────────────────────────────────────────
const getStatusLabel = (statusCode: number): string => {
  switch (statusCode) {
    case WorkOrderStatus.Paid:    return "Paid";
    case WorkOrderStatus.Cancel:  return "Canceled";
    case WorkOrderStatus.Pending: return "Pending";
    default:                      return "Unknown Status";
  }
};

// ─── Interfaces ──────────────────────────────────────────────────────────────
interface IProduct {
  productName: string;
  productDescription: string;
  productImg: String | null;
  numberOfPassengers: number | null;
  transferType: string;
  transferTypeFrom: string;
  transferTypeTo: string;
  transferTypeDescription: string;
  departureDateTime: Dayjs | null;
  returnDateTime: Dayjs | null;
  departureAddress: string;
  destinationAddress: string;
  multiDestination: string;
  quantity: number | null;
  price: number | null;
  tax: number | null;
  taxAmount: number | null;
  total: number;
  taxId: number | null;
  unitPriceWithDiscount: number | null;
  unitTotalWithDiscount: number;
  productType: string;
  productLongDescription: string;
  productShortDescription: string;
  pickups?: { address: string; city: string; dateTime: dayjs.Dayjs | null }[];
  dropoffs?: { address: string; city: string; dateTime: dayjs.Dayjs | null }[];
  returnPickups?: { address: string; city: string; dateTime: dayjs.Dayjs | null }[];
  returnDropoffs?: { address: string; city: string; dateTime: dayjs.Dayjs | null }[];
  useSameAsOutbound?: boolean;
}

interface IDiscount {
  value: number | null;
  description: string;
}

interface IQuoteWorkOrder {
  id: number;
  quoteWorkOrderCompanyId: number;
  quoteWorkOrderCustomerId: string;
  quoteWorkOrderCustomerProjectId: string;
  quoteWorkOrderNo: string;
  quoteWorkOrderIssueDate: Dayjs;
  quoteWorkOrderDueDate: Dayjs;
  quoteTripId: string;
  quoteTripName: string;
  quoteWorkOrderAmount: number | null;
  quoteWorkOrderStatus: number;
  quoteWorkOrderSale: IProduct[];
  quoteWorkOrderDiscount: string;
  quoteWorkOrderSubTotal: string;
  quoteWorkOrderSaleGrandTotal: string;
  quoteWorkOrderSaleDiscountDescription: IDiscount[];
  customer: ICustomer;
  company: ICompanyDetails;
  project: ICustomerProject;
  quoteWorkOrderCustomerRemark: string;
  quoteWorkOrderCustomerMemo: string;
  quoteWorkOrderSaleDiscount: string;
  quoteWorkOrderSaleSubTotal: string;
  created_by: number;
  created_at: string;
  mainStatus: number;
  invoiceId: number;
  InvoiceNo: string;
  amount_paid: number;
}

interface ViewWorkOrderProps {
  quoteId: string;
  type: string;
}

// ─── Component ───────────────────────────────────────────────────────────────
const ViewWorkOrder: React.FC<ViewWorkOrderProps> = ({ quoteId, type }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const { id: QuoteId } = useParams<{ id: string }>();
  useEffect(() => {}, [QuoteId]);
  const componentRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [page] = useState(0);
  const [rowsPerPage] = useState(100);
  const [newEmailInvoice, { isLoading: isEmailSending }] = useCreateEmailInvoiceMutation();
  const { showErrorMessage, showMessage } = useNotifier();

  const [dataLoaded, setDataLoaded] = useState(false);
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalTax, setTotalTax] = useState(0);

  const {
    data: workOrderData,
    isLoading: workIsLoading,
    isError: workOrderError,
    refetch: workOrderRefetch,
  } = useGetWorkOrderQuery({ searchText: "", id: String(quoteId) });

    const {
        data: invoiceData,
        isLoading: invoiceIsLoading,
        isError: invoiceError,
        refetch: invoiceRefetch,
      } = useGetAllInvoiceQuery({
        searchText: "",
        id: "",
        quoteWorkOrderCustomerId: "",
        quoteWorkOrderCustomerProjectId: "",
      });

  const WorkOrderDataList = useMemo(() => {
    if (workOrderData && Array.isArray(workOrderData.data) && workOrderData.data.length > 0) {
      return workOrderData.data[0] as IQuoteWorkOrder;
    }
    return null;
  }, [workOrderData]);

   const invoiceList = useMemo(() => {
      if (
        invoiceData &&
        Array.isArray(invoiceData.data) &&
        invoiceData.data.length > 0
      ) {
        // Find and return the first matching invoice
        const matchingInvoice = invoiceData.data.find(
          (invoice: IInvoice) => String(invoice.work_order_id) === String(QuoteId)
        );
  
        return matchingInvoice || null; // Return null if no match is found
      }
      return null; // Return null if invoiceData is not valid
    }, [invoiceData, QuoteId]);
  

  const coursings: IProduct[] = Array.isArray(WorkOrderDataList?.quoteWorkOrderSale)
    ? WorkOrderDataList!.quoteWorkOrderSale
    : [];

  const discountList: IDiscount[] = Array.isArray(WorkOrderDataList?.quoteWorkOrderSaleDiscountDescription)
    ? WorkOrderDataList!.quoteWorkOrderSaleDiscountDescription
    : [];

  useEffect(() => {
    if (WorkOrderDataList && WorkOrderDataList.project) {
      setDataLoaded(true);
    } else {
      setDataLoaded(false);
    }
    console.log(quoteId);
  }, [workOrderData]);

  useEffect(() => {
    let newTotalPrice = 0;
    let newTotalTax = 0;
    coursings.forEach((product) => {
      newTotalPrice += product.price ?? 0;
      newTotalTax += product.taxAmount ?? 0;
    });
    setTotalPrice(newTotalPrice);
    setTotalTax(newTotalTax);
  }, [coursings]);

  const handleSendEmail = async () => {
    try {
      if (!componentRef.current) { showErrorMessage("The invoice component is not available."); return; }
      if (!dataLoaded) { showErrorMessage("Data is still loading. Please wait."); return; }
      if (!WorkOrderDataList || !WorkOrderDataList.project) {
        showErrorMessage("Required work order or project data is missing."); return;
      }
      const pdf = new jsPDF();
      const canvas = await html2canvas(componentRef.current);
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      const pdfBlob = pdf.output("blob");
      const fileName = `${WorkOrderDataList?.InvoiceNo}.pdf`;
      const file = new File([pdfBlob], fileName, { type: "application/pdf" });
      const formData = new FormData();
      formData.append("customer_id", WorkOrderDataList?.project.customerId.toString() || "");
      formData.append("project_id", WorkOrderDataList?.project.id.toString() || "");
      formData.append("to", WorkOrderDataList?.project.projectEmail || "");
      formData.append("subject", "Invoice for Your Project");
      formData.append("message", "Please find attached the invoice for your project.");
      formData.append("invoice", file);
      const emailResponse = await newEmailInvoice(formData).unwrap();
      if (!emailResponse) { showErrorMessage("Failed to send email"); }
      else { showMessage("Email sent successfully"); }
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  // ─── Style constants ────────────────────────────────────────────────────────
  const BORDER = "1px solid #bbb";
  const FONT_SM = "0.82rem";
   // PDF uses a thin light-grey horizontal rule for top/bottom — not a thick band
  const DOC_BORDER_COLOR = "#b0b8c1";   // thin grey line matching the PDF scanned look
  const DOC_BORDER_STYLE = `3px solid ${DOC_BORDER_COLOR}`;

 const tdLabel: React.CSSProperties = {
  border: BORDER, padding: "7px 10px", fontWeight: "bold",
  whiteSpace: "nowrap", fontSize: FONT_SM, verticalAlign: "middle",
};
const tdValue: React.CSSProperties = {
  border: BORDER, padding: "7px 10px",
  fontWeight: "bold",                   // ← added
  fontSize: FONT_SM, verticalAlign: "middle",
};
const thStyle: React.CSSProperties = {
  border: BORDER, padding: "8px 10px",
  fontWeight: "bold", fontSize: FONT_SM, textAlign: "left",
};
const tdBordered: React.CSSProperties = {
  border: BORDER, padding: "8px 10px",
  fontWeight: "bold",                   // ← added
  fontSize: FONT_SM, verticalAlign: "top",
};
const innerCell: React.CSSProperties = {
  padding: "8px 10px", borderRight: BORDER,
  fontWeight: "bold",                   // ← added
  fontSize: FONT_SM, verticalAlign: "top", wordBreak: "break-word",
};
const innerCellLast: React.CSSProperties = {
  padding: "8px 10px",
  fontWeight: "bold",                   // ← added
  fontSize: FONT_SM, verticalAlign: "top", wordBreak: "break-word",
};

  // ─── Helpers ────────────────────────────────────────────────────────────────
  const isProject = !!WorkOrderDataList?.quoteWorkOrderCustomerProjectId;
  const resolveField = (projectVal?: string, customerVal?: string) =>
    isProject ? (projectVal || "") : (customerVal || "");

const formatPhone = (num: string): string => {
  // Remove all non-digits first
  const digits = num.replace(/\D/g, "");
  // Format as XXX-XXX-XXXX
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // Format as 1-XXX-XXX-XXXX (11 digits with country code)
  if (digits.length === 11) {
    return `${digits.slice(0, 1)}-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return num; // return as-is if unrecognised format
};

const buildPhoneString = (): string => {
  const primary   = formatPhone(WorkOrderDataList?.company?.companyMobileNumber || "");
  const alternate = formatPhone((WorkOrderDataList?.company as any)?.companyAltMobileNumber || "");
  if (primary && alternate) return `${primary} / ${alternate}`;
  return primary || alternate;
};

  // ─── Start / End time resolution ─────────────────────────────────────────
  // Priority order:
  //   1. product.departureDateTime / returnDateTime  (direct fields)
  //   2. first pickup dateTime  (start) / first dropoff dateTime (end)
  //   3. fallback empty string
  const resolveTime = (dt: Dayjs | null | undefined): string => {
    if (dt && dayjs(dt).isValid()) return dayjs(dt).format("h A");
    return "";
  };

  const firstProduct = coursings[0];

 const busStartTime = (() => {
  const direct = resolveTime(firstProduct?.departureDateTime);
  if (direct) return direct;
  for (const p of coursings) {
    const found = (p.pickups || []).find(r => r.dateTime && dayjs(r.dateTime).isValid());
    if (found?.dateTime) return dayjs(found.dateTime).format("h A");
  }
  return "";
})();

const busEndTime = (() => {
  const direct = resolveTime(firstProduct?.returnDateTime);
  if (direct) return direct;
  // Collect ALL times across every product and location type, pick the latest
  const allTimes: dayjs.Dayjs[] = [];
  for (const p of coursings) {
    [...(p.pickups || []), ...(p.dropoffs || []), ...(p.returnPickups || []), ...(p.returnDropoffs || [])]
      .forEach(r => { if (r.dateTime && dayjs(r.dateTime).isValid()) allTimes.push(dayjs(r.dateTime)); });
  }
  if (allTimes.length > 0) {
    allTimes.sort((a, b) => b.valueOf() - a.valueOf());
    return allTimes[0].format("h A");
  }
  return "";
})();

  type TripRow = { label: string; addr: string; time: string };

const buildTripRows = (product: IProduct): TripRow[] => {
  const pickups     = (product.pickups || []).filter(p => p.address || p.city);
  const dropoffs    = (product.dropoffs || []).filter(d => d.address || d.city);
  const retPickups  = (product.returnPickups || []).filter(p => p.address || p.city);
  const retDropoffs = (product.returnDropoffs || []).filter(d => d.address || d.city);

  const rows: TripRow[] = [
    ...pickups.map((r, i) => ({
      label: pickups.length > 1 ? `${product.productName} Pickup ${i + 1}` : product.productName,
      addr:  [r.address].filter(Boolean).join(", "),
      time:  r.dateTime && dayjs(r.dateTime).isValid() ? dayjs(r.dateTime).format("h a") : "",
    })),
    ...dropoffs.map((r, i) => ({
      label: `Dropoff${dropoffs.length > 1 ? ` ${i + 1}` : ""}`,
      addr:  [r.address].filter(Boolean).join(", "),
      time:  r.dateTime && dayjs(r.dateTime).isValid() ? dayjs(r.dateTime).format("h a") : "",
    })),
    ...retPickups.map((r, i) => ({
      label: retPickups.length > 1 ? `Return Pickup ${i + 1}` : "Return Pickup",
      addr:  [r.address].filter(Boolean).join(", "),
      time:  r.dateTime && dayjs(r.dateTime).isValid() ? dayjs(r.dateTime).format("h a") : "",
    })),
    ...retDropoffs.map((r, i) => ({
      label: retDropoffs.length > 1 ? `Return Dropoff ${i + 1}` : "Return Dropoff",
      addr:  [r.address].filter(Boolean).join(", "),
      time:  r.dateTime && dayjs(r.dateTime).isValid() ? dayjs(r.dateTime).format("h a") : "",
    })),
  ];

  // fallback — use departure/destination fields directly
  if (rows.length === 0) {
    if (product.departureAddress)
      rows.push({
        label: product.productName,
        addr:  product.departureAddress,
        time:  product.departureDateTime && dayjs(product.departureDateTime).isValid()
                 ? dayjs(product.departureDateTime).format("h a") : "",
      });
    if (product.destinationAddress)
      rows.push({
        label: "Destination",
        addr:  product.destinationAddress,
        time:  product.returnDateTime && dayjs(product.returnDateTime).isValid()
                 ? dayjs(product.returnDateTime).format("h a") : "",
      });
  }
  return rows;
};
  
  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
       <style>{`
      @media print {
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      }
    `}</style>
    
      <Grid container spacing={2}>

        {/* Top toolbar card */}
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.quotesCardInvoice}>
            <Grid container spacing={1} padding="2px">
              <Grid item lg={12} md={12} sm={12} xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Grid container spacing={1} justifyContent="flex-end" sx={{ display: "flex" }} />
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* ── PRINTABLE DOCUMENT ──────────────────────────────────────────── */}
        <Grid item lg={12} md={12} sm={12} xs={12}>
          {/*
            FIX 3 — No horizontal scrollbar on wide screens.
            overflowX:"auto" means a scrollbar appears ONLY when the viewport
            is narrower than minWidth (700 px).  On normal desktop widths the
            document fills the container without any scrollbar.
          */}
          <div style={{ overflowX: "auto", width: "100%" }}>
            <div ref={componentRef} style={{ minWidth: "700px" }}>
              <Card
                style={{
                  position: "relative",
                  boxSizing: "border-box",
                  backgroundColor: theme.palette.cardAdd?.bgColor || "#fff",
                  boxShadow: "none",
                  width: "100%",
                  margin: "0 auto",
                }}
              >

                {/* ══════════════════════════════════════════════════════════
                    HEADER — matches PDF exactly
                    ┌──────────────────────────────────────────────────────┐
                    │ [Logo]               CHARTER CONFIRMATION            │
                    │ 11 BENLEIGH DR…                                      │
                    │ tjbusline@…  Tel:…                                   │
                    └──────────────────────────────────────────────────────┘
                ═══════════════════════════════════════════════════════════ */}
                <div style={{
  height: "3px",
  backgroundColor: DOC_BORDER_COLOR,
  WebkitPrintColorAdjust: "exact" as any,
  printColorAdjust: "exact" as any,
}} />
<div>
                  {/* Row 1 — Logo (left) + Title (right) */}
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    padding: "14px 20px 4px 20px",
                  }}>
                    <div>
                      <img
                        src={WorkOrderDataList?.company?.CompanyLogo
                          ? `${LARAVEL_API_URL}/${WorkOrderDataList.company.CompanyLogo}` : ""}
                        alt="Company Logo"
                        className={classes.invoiceImg}
                        style={{ 
                          // maxHeight: "120px", 
                          maxWidth: "400px", objectFit: "contain", display: "block" }}
                      />
                    </div>
                    <div style={{ textAlign: "right", paddingLeft: "16px" }}>
                      <Typography style={{ fontSize: "20px", fontWeight: "bold", letterSpacing: "3px" }}>
                        CHARTER CONFIRMATION
                      </Typography>
                    </div>
                  </div>

                  {/* Row 2 — Company address + contact (left-aligned, below logo) */}
                  <div style={{ padding: "2px 20px 14px 20px" }}>
                    <Typography style={{ fontSize: FONT_SM, fontWeight: "600" }}>
                      {[
                        WorkOrderDataList?.company?.companyStreetNumber,
                        WorkOrderDataList?.company?.companyStreetName,
                        WorkOrderDataList?.company?.companyCity,
                        WorkOrderDataList?.company?.companyProvince,
                        WorkOrderDataList?.company?.companyPostalCode,
                      ].filter(Boolean).join(", ")}
                    </Typography>
                    {/* email + Tel on one line, blue underline like the PDF */}
                  <Typography style={{ 
  fontSize: "1.0rem",           // bigger than current 0.88rem
  fontWeight: "bold",
  color: "#1155CC", 
  textDecoration: "underline",
}}>
                      {WorkOrderDataList?.company?.companyEmail || ""}
                      {buildPhoneString()
                      ? `\u00A0\u00A0Tel:${buildPhoneString()}`
                      : ""}
                    </Typography>
                  </div>
                </div>
                {/* ══ END HEADER ══════════════════════════════════════════ */}

                {/* ══ BODY ════════════════════════════════════════════════ */}
                <div style={{ padding: "16px 16px 16px 16px" }}>

                  {/* ── INFO GRID TABLE ─────────────────────────────────── */}
                  <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "16px" }}>
                    <colgroup>
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "35%" }} />
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "35%" }} />
                    </colgroup>
                    <tbody>
                      <tr>
                        <td style={tdLabel}>Trip ID:</td>
                        <td style={tdValue}>{WorkOrderDataList?.quoteTripId || ""}</td>
                        <td style={tdLabel}>Trip Name:</td>
                        <td style={tdValue}>{WorkOrderDataList?.quoteTripName || ""}</td>
                      </tr>
                      <tr>
                        <td style={tdLabel}>Purchase Order #:</td>
                        <td style={tdValue}>{invoiceList?.po_number || ""}</td>
                        <td style={tdLabel}>Trip Date:</td>
                        <td style={tdValue}>
                            {(() => {
    const firstSale = WorkOrderDataList?.quoteWorkOrderSale?.[0];
    
    // Try new pickup structure first
    const pickupDateTime = firstSale?.pickups?.[0]?.dateTime;
    
    // Fallback to departureDateTime if pickup structure doesn't exist
    const tripDate = pickupDateTime || firstSale?.departureDateTime;
    
    return tripDate 
      ? dayjs(tripDate).format("MMMM D/YYYY").toUpperCase()
      : "";
  })()}
                          {/* {WorkOrderDataList?.quoteWorkOrderIssueDate
                            ? dayjs(WorkOrderDataList.quoteWorkOrderIssueDate).format("MMMM D/YYYY").toUpperCase()
                            : ""} */}
                        </td>
                      </tr>
                      <tr>
                        <td style={tdLabel}>Customer</td>
                        <td style={tdValue}>
                          <strong>
                            {isProject
                              ? (WorkOrderDataList?.project?.projectName || "")
                              : `${WorkOrderDataList?.customer?.customerFirstName || ""} ${WorkOrderDataList?.customer?.customerLastName || ""}`.trim()}
                          </strong>
                        </td>
                        <td style={tdLabel}>e-mail:</td>
                        <td style={tdValue}>
                          {resolveField(WorkOrderDataList?.project?.projectEmail, WorkOrderDataList?.customer?.customerEmail)}
                        </td>
                      </tr>
                      <tr>
                        <td style={tdLabel}>Attention:</td>
                        <td style={tdValue}></td>
                        <td style={tdLabel}>State/Province:</td>
                        <td style={tdValue}>
                          {resolveField(WorkOrderDataList?.project?.projectProvince, WorkOrderDataList?.customer?.customerProvince)}
                        </td>
                      </tr>
                      <tr>
                        <td style={tdLabel}>Address:</td>
                        <td style={tdValue}>
                          {isProject
                            ? [
                                WorkOrderDataList?.project?.projectStreetNumber,
                                WorkOrderDataList?.project?.projectStreetName,
                              ].filter(Boolean).join(" ") +
                              (WorkOrderDataList?.project?.projectCity ? `, ${WorkOrderDataList.project.projectCity}` : "") +
                              (WorkOrderDataList?.project?.projectProvince ? `, ${WorkOrderDataList.project.projectProvince}` : "") +
                              (WorkOrderDataList?.project?.projectPostalCode ? ` ${WorkOrderDataList.project.projectPostalCode}` : "")
                            : [
                                WorkOrderDataList?.customer?.customerStreetNumber,
                                WorkOrderDataList?.customer?.customerStreetName,
                              ].filter(Boolean).join(" ")}
                        </td>
                        <td style={tdLabel}>Zip/Postal Code:</td>
                        <td style={tdValue}>
                          {resolveField(WorkOrderDataList?.project?.projectPostalCode, WorkOrderDataList?.customer?.customerPostalCode)}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ ...tdValue, border: BORDER }}></td>
                        <td style={{ ...tdValue, border: BORDER }}></td>
                        <td style={tdLabel}>Phone:</td>
                        <td style={tdValue}>
                          {resolveField(WorkOrderDataList?.project?.projectPhoneNumber, WorkOrderDataList?.customer?.customerMobileNum)}
                        </td>
                      </tr>
                      <tr>
                        <td style={tdLabel}>City:</td>
                        <td style={tdValue}>
                          {resolveField(WorkOrderDataList?.project?.projectCity, WorkOrderDataList?.customer?.customerCity)}
                        </td>
                        <td style={{ ...tdValue, border: BORDER }}></td>
                        <td style={{ ...tdValue, border: BORDER }}></td>
                      </tr>
                    </tbody>
                  </table>

                  {/* ── TRIP / BUS / FINANCIALS TABLE ─────────────────────
                      Outer table: 3 cols (ADDRESS DESCRIPTION | ADDRESS | DEPARTURE TIME)
                      Bus row & Financials row: nested tables with
                      table-layout:fixed + 4×25% cols → perfectly uniform separators
                  ──────────────────────────────────────────────────────── */}
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <colgroup>
                      <col style={{ width: "28%" }} />
                      <col style={{ width: "57%" }} />
                      <col style={{ width: "15%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={thStyle}>ADDRESS DESCRIPTION</th>
                        <th style={thStyle}>ADDRESS</th>
                        <th style={{ ...thStyle, textAlign: "center", whiteSpace:'nowrap' }}>DEPARTURE TIME</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Trip data rows */}
                      {workOrderError || coursings.length === 0 ? (
                        <tr>
                          <td colSpan={3} style={{ ...tdBordered, textAlign: "center" }}>
                            {workOrderError ? "Error fetching data" : "No data available"}
                          </td>
                        </tr>
                      ) : (
                        coursings
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .flatMap((product, index) => {
                            const rows = buildTripRows(product);
                            if (rows.length === 0) {
                              return [(
                                <tr key={`${index}-empty`}>
                                  <td style={tdBordered}><strong>{product.productName}</strong></td>
                                  <td style={tdBordered}></td>
                                  <td style={{ ...tdBordered, textAlign: "center" }}></td>
                                </tr>
                              )];
                            }
                            return rows.map((row, rIdx) => (
                              <tr key={`${index}-${rIdx}`}>
                                <td style={tdBordered}><strong>{row.label}</strong></td>
                                <td style={tdBordered}>{row.addr}</td>
                                <td style={{ ...tdBordered, textAlign: "center" }}>{row.time}</td>
                              </tr>
                            ));
                          })
                      )}

                      {/* Special Notification */}
                      <tr>
                        <td colSpan={3} style={{ ...tdBordered, fontWeight: "bold" }}>
                          Special Notification.
                        </td>
                      </tr>

                      {/* ── Bus details row ──────────────────────────────
                          FIX 1: table-layout:fixed + 4 × 25% colgroup
                          ensures all 4 column dividers are perfectly uniform.
                      ─────────────────────────────────────────────────── */}
                      <tr>
                        <td colSpan={3} style={{ ...tdBordered, padding: 0 }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                            <colgroup>
                              <col style={{ width: "25%" }} />
                              <col style={{ width: "25%" }} />
                              <col style={{ width: "25%" }} />
                              <col style={{ width: "25%" }} />
                            </colgroup>
                            <tbody>
                              <tr>
                                <td style={innerCell}>
                                  <strong>Bus Type:</strong><br />
                                  {firstProduct?.productName || "SCHOOL BUS"}
                                </td>
                                <td style={innerCell}>
                                  <strong>Bus Quantity:</strong> {firstProduct?.quantity ?? 1}
                                </td>
                                <td style={innerCell}>
                                  <strong>Start Time:</strong> {busStartTime}
                                </td>
                                <td style={innerCellLast}>
                                  <strong>End Time:</strong> {busEndTime}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>

                      {/* ── Financials row ───────────────────────────────
                          FIX 1 (same): table-layout:fixed + 4 × 25% ensures
                          Subtotal | Additional | TAX | Total separators line
                          up exactly with the Bus row separators above.
                      ─────────────────────────────────────────────────── */}
                      <tr>
                        <td colSpan={3} style={{ ...tdBordered, padding: 0 }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                            <colgroup>
                              <col style={{ width: "25%" }} />
                              <col style={{ width: "25%" }} />
                              <col style={{ width: "25%" }} />
                              <col style={{ width: "25%" }} />
                            </colgroup>
                            <tbody>
                              <tr>
                                <td style={innerCell}>
                                  <strong>Subtotal $</strong> {totalPrice.toFixed(2)}
                                </td>
                                <td style={innerCell}>
                                  <strong>Additional---------:</strong>
                                  {discountList.length > 0 && (discountList[0].value ?? 0) !== 0
                                    ? ` $${(discountList[0].value ?? 0).toFixed(2)}` : ""}
                                </td>
                                <td style={innerCell}>
                                  <strong>TAX:</strong> {totalTax.toFixed(2)}
                                </td>
                                <td style={innerCellLast}>
                                  <strong>Total:</strong>{" "}
                                  {parseFloat(WorkOrderDataList?.quoteWorkOrderSaleGrandTotal || "0").toFixed(2)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>

                    </tbody>
                  </table>

                

                  {WorkOrderDataList?.quoteWorkOrderCustomerMemo?.trim() && (
                    <Typography variant="body2" style={{ marginTop: "12px", fontSize: FONT_SM }}>
                      Note: {WorkOrderDataList.quoteWorkOrderCustomerMemo}
                    </Typography>
                  )}

                {/* Notes — rendered from companyTermsAndConditions, one line per paragraph */}
{WorkOrderDataList?.company?.companyTermsAndConditions?.trim() && (
  <div style={{ marginTop: "10px" }}>
    {WorkOrderDataList.company.companyTermsAndConditions
      .split("\n")
      .filter(line => line.trim() !== "")
      .map((line, i) => (
        <Typography key={i} variant="body2" style={{ fontWeight: "bold", fontSize: FONT_SM }}>
          {line}
        </Typography>
      ))}
  </div>
)}

                  {/* Signature line */}
                  <Box display="flex" justifyContent="space-between" style={{ marginTop: "28px" }}>
                    <Typography style={{ fontWeight: "bold", fontSize: FONT_SM }}>Signature …………………………………………………………</Typography>
                    <Typography style={{ fontWeight: "bold", fontSize: FONT_SM }}>Date ………………………………………………</Typography>
                  </Box>

                  {WorkOrderDataList?.company?.companyCancellationPolicy?.trim() && (
                    <Typography variant="body2" style={{ marginTop: "12px", fontSize: FONT_SM }}>
                      {WorkOrderDataList.company.companyCancellationPolicy}
                    </Typography>
                  )}

                  {/* Watermark */}
                  {WorkOrderDataList?.company.CompanyWatermark && (
                    <div style={{
                    //   position: "absolute", top: "50%", left: "50%",
                    //   transform: "translate(-50%, -50%)", width: "100%", height: "100%",
                    //   display: "flex", justifyContent: "center", alignItems: "center",
                    //   zIndex: 0, pointerEvents: "none", overflow: "hidden",
                      position: "absolute", bottom: 0, left: 0,
                      width: "100%", height: "100%",
                      display: "flex", justifyContent: "flex-start", alignItems: "flex-end",
                      zIndex: 0, pointerEvents: "none", overflow: "hidden",
                   
                   }}>
                      <img
                        src={`${LARAVEL_API_URL}/${WorkOrderDataList.company.CompanyWatermark}`}
                        alt="Watermark"
                        style={{ maxWidth: "70%", 
                         // opacity: 0.15 
                        }}
                      />
                    </div>
                  )}

                </div>{/* end body */}

                {/* ══ RED BOTTOM BORDER + FOOTER ══════════════════════════ */}
                <div style={{ borderBottom: DOC_BORDER_STYLE, padding: "16px 20px" }}>
                  <Typography variant="body2" align="center">
                    {WorkOrderDataList?.company.companyFooterDetails && (
                      <>
                        {WorkOrderDataList.company.companyFooterDetails.split("\n").map((line, i) => (
                          <span key={i}>{line}<br /></span>
                        ))}
                      </>
                    )}
                    {/* {WorkOrderDataList?.company.companyHstNo && (
                      <>HST No: ( {WorkOrderDataList.company.companyHstNo} )<br /></>
                    )} */}
                 
                  </Typography>
                </div>

              </Card>
            </div>{/* end minWidth wrapper */}
          </div>{/* end overflowX wrapper */}
        </Grid>

        {/* Print button */}
        {type !== "email" && (
          <Grid item lg={12} md={12} sm={12} xs={12} padding="10px">
            <Grid container>
              <Grid item lg={12} md={12} sm={12} xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Grid container justifyContent="flex-end" sx={{ display: "flex" }}>
                  <Grid item>
                    <ReactToPrint
                      trigger={() => (
                        <Button variant="contained" startIcon={<LocalPrintshopSharpIcon />} className={classes.addButton}>
                          Print
                        </Button>
                      )}
                      content={() => componentRef.current}
                      documentTitle={WorkOrderDataList?.quoteWorkOrderNo || "Work Order"}
                   pageStyle={`
    @page {
      margin: 8mm;
      size: auto;
    }
   
  `}
                  />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

      </Grid>
    </div>
  );
};

export default ViewWorkOrder;
